import type { ReadinessBlocker } from "./wire-types";

// ---------------------------------------------------------------------------
// V17 — the one piece of arithmetic the score's flyout does, in a pure module so
// it can be asserted.
//
// IT IS HERE BECAUSE THE FIRST VERSION WAS WRONG ON SCREEN AND RIGHT IN EVERY
// TEST. The summary line read "6 things are holding this back, worth 42.2 of 24
// weight" — an amount at risk nearly double the total weight there is to lose,
// which is not a number a reader can do anything with except distrust.
//
// The cause: `score_impact` is a property of the REQUIREMENT (weight x readiness
// deficit), and one requirement can raise several blockers. A post short of people
// with nobody on standby raises two, and an unfilled post whose offers have gone
// stale raises three. Summing over blockers therefore counts the same post's
// deficit once per blocker.
//
// Nothing caught it. The unit tests asserted the ranking order and the
// reconciliation identity, both of which were correct; the SQL matrix asserted the
// score, which was correct; the gallery registry asserted the screen was
// registered, which it was. It was found by opening the gallery and reading the
// sentence — the same way run 21 found that every parameterised screen had been
// rendering with an undefined route parameter for eight slices.
// ---------------------------------------------------------------------------

/**
 * How much weight the open blockers are actually costing, in [0, weight_total].
 *
 * DE-DUPLICATED BY REQUIREMENT, because `score_impact` describes the post rather
 * than the blocker. Two blockers on one post are two things to do about ONE
 * deficit, not two deficits.
 */
export function weightAtRisk(blockers: readonly ReadinessBlocker[]): number {
  const perRequirement = new Map<string, number>();
  for (const b of blockers) {
    // The max rather than the first: every blocker on a requirement should carry
    // the same impact, and if one ever does not, reporting the largest is the
    // reading that cannot understate what is at stake.
    const seen = perRequirement.get(b.requirement_id);
    if (seen === undefined || b.score_impact > seen) perRequirement.set(b.requirement_id, b.score_impact);
  }
  let total = 0;
  for (const impact of perRequirement.values()) total += impact;
  return Math.round(total * 100) / 100;
}

/** How many distinct posts the blockers are about. */
export function postsAtRisk(blockers: readonly ReadinessBlocker[]): number {
  return new Set(blockers.map((b) => b.requirement_id)).size;
}
